/**
 * usePublicSetting — يقرأ مفتاحاً من app_settings عبر Supabase (السياسة تسمح بقراءة `public_*` للجميع).
 * يُستخدم لإظهار/إخفاء عناصر واجهة بناءً على إعدادات قابلة للتعديل من الأدمن دون نشر.
 *
 * الأداء: الاستدعاءات المتزامنة تتشارك طلباً واحداً (SELECT لكلّ الصفوف المسموحة)
 * بدل طلب REST لكلّ مفتاح — بلا cache للاستجابات المكتملة.

 * تبقى queryKey لكلّ مفتاح مستقلّة لتعمل invalidateQueries الحاليّة في لوحات الأدمن.
 *
 * دعم البيئات: القيم قد تُخزَّن كـ `{ dev, staging, prod }` — نختار قيمة البيئة
 * الحاليّة (المكتشفة من hostname) بحيث لا يُخلط تفعيل المعاينة مع الإنتاج.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { envFromWindow, pickEnvValue } from "@/lib/env-detect";

// دمج الطلبات المتزامنة فقط (in-flight) — بلا cache للاستجابات المكتملة،
// حتّى لا يُقدَّم صفّ قديم بعد حفظ الأدمن ويُعتبر «حديثاً» لدقيقة.
let sharedInflight: Promise<Record<string, unknown>> | null = null;

async function fetchAllPublicSettings(): Promise<Record<string, unknown>> {
  if (sharedInflight) return sharedInflight;
  sharedInflight = (async () => {
    // RLS يُرجع فقط الصفوف المسموح قراءتها لهذا الزائر (public_* للجميع)
    const { data, error } = await supabase.from("app_settings").select("key, value");
    const rows: Record<string, unknown> = {};
    if (!error && data) for (const r of data) rows[r.key as string] = r.value;
    return rows;
  })();
  try {
    return await sharedInflight;
  } finally {
    sharedInflight = null;
  }
}


export function usePublicSetting<T = unknown>(key: string, fallback: T) {
  const env = envFromWindow();
  const { data } = useQuery({
    queryKey: ["public-setting", key, env],
    queryFn: async (): Promise<T> => {
      const rows = await fetchAllPublicSettings();
      if (!(key in rows)) return fallback;
      const picked = pickEnvValue(rows[key], env);
      if (picked === undefined || picked === null) return fallback;
      return picked as T;
    },
    staleTime: 60_000,
  });
  return data ?? fallback;
}
